/**
 * Handles the table of contents (TOC) behavior by updating active links as sections come into view.
 *
 * @example
 * // Initialize a TOC handler for a sidebar with a specific offset.
 * const sidebarTOC = new TocHandler({
 *     tocContainerSelector: '.on-this-page--sidebar[data-toc]',
 *     offset: 100
 * });
 *
 * @example
 * // Initialize a mobile TOC handler with a callback function to close a dropdown.
 * const mobileTOC = new TocHandler({
 *     tocContainerSelector: '.on-this-page--mobile-toc[data-toc]',
 *     offset: 50,
 *     callbacks: () => window.ASB_App.dropdownHandler.closeDropdown('#mobile-toc-content')
 * });
 *
 * @access public
 */
class TocHandler {
    /**
     * Creates an instance of TocHandler.
     * @access public
     * @param {Object} options - Configuration options for the TOC handler.
     * @param {string} [options.tocContainerSelector='[data-toc]'] - Selector for the TOC container.
     * @param {number} [options.offset=150] - Offset from the top of the viewport for active state calculation.
     * @param {string} [options.activeClass='active'] - CSS class to apply to the active link.
     * @param {string} [options.marginRoot='-80%'] - Root margin for the IntersectionObserver.
     * @param {string} [options.linkSelector='a[data-section]'] - Selector for TOC links.
     * @param {string} [options.sectionAttribute='data-section'] - Attribute containing the section ID.
     * @param {Function} [options.callbacks=() => {}] - Callback function invoked on link interaction.
     */
    constructor({
        tocContainerSelector = '[data-toc]',
        offset = 150,
        activeClass = 'active',
        marginRoot = '-80%',
        linkSelector = 'a[data-section]',
        sectionAttribute = 'data-section',
        callbacks = () => {}
    } = {}) {
        this.tocContainers = document.querySelector(tocContainerSelector);

        if (this.tocContainers === null) {
            console.warn("TocHandler: No TOC containers found.");
            return;
        }

        this.offset = offset;
        this.activeClass = activeClass;
        this.marginRoot = marginRoot;
        this.linkSelector = linkSelector;
        this.sectionAttribute = sectionAttribute;
        this.observers = [];
        this.currentActiveLinks = new Map();
        this.linkPos = 0;
        this.callbacks = callbacks;
        this.tocLinks = [...this.tocContainers.querySelectorAll(this.linkSelector)];
        this.headers = this.tocLinks
            .map(link => document.querySelector(`#${link.getAttribute(this.sectionAttribute)}`))
            .filter(header => header !== null);

        if (this.headers.length && this.tocLinks.length) {
            this.init();
        } else {
            console.warn("TocHandler: No valid elements or headers found.");
        }
    }

    /**
     * Initializes the TocHandler instance.
     * Sets up the IntersectionObserver and attaches click event listeners.
     * @access private
     */
    init() {
        this.initObserver();
        this.addClickListeners();
    }

    /**
     * Sets up an IntersectionObserver to observe headers and handle scrolling interactions.
     * @access private
     */
    initObserver() {
        const options = {
            root: null,
            rootMargin: `-${this.offset}px 0px ${this.marginRoot} 0px`,
            threshold: 0,
        };

        const observer = new IntersectionObserver((entries) => {
            this.handleIntersect(entries);
        }, options);

        this.headers.forEach(header => observer.observe(header));

        this.observers.push(observer);
    }

    /**
     * Handles the intersection of observed elements.
     * Determines which header is currently in view and updates the active link accordingly.
     * @access private
     * @param {IntersectionObserverEntry[]} entries - Array of intersection observer entries.
     */
    handleIntersect(entries) {
        entries.forEach(entry => {
            const id = entry.target.id;
            const linkIndex = this.tocLinks.findIndex(link =>
                link.getAttribute(this.sectionAttribute) === id
            );

            if (entry.isIntersecting) {
                this.updateActiveLink(linkIndex);
            }else if (entry.boundingClientRect.top > this.offset) {
                const prevIndex = Math.max(this.linkPos - 1, 0);
                this.updateActiveLink(prevIndex);
            }
        });
    }

    /**
     * Updates the active link in the table of contents based on the provided index.
     * @access private
     * @param {number} newIndex - Index of the link to be activated.
     */
    updateActiveLink(newIndex) {
        const currentActiveLink = this.currentActiveLinks.get(this.tocLinks) || null;

        if (currentActiveLink) {
            currentActiveLink.classList.remove(this.activeClass);
        }

        const newActiveLink = this.tocLinks[newIndex];
        if (newActiveLink) {
            newActiveLink.classList.add(this.activeClass);
            this.currentActiveLinks.set(this.tocLinks, newActiveLink);
            this.linkPos = newIndex;
        }

    }

    /**
     * Adds click event listeners to the TOC links to handle smooth scrolling.
     * @access private
     */
    addClickListeners() {
        this.tocLinks.forEach(link => {
            link.addEventListener('click', this.handleDocJump.bind(this));
        });
    }

    /**
     * Handles the click event on a TOC link.
     * Scrolls smoothly to the target section and invokes the callback function.
     * @access private
     * @param {Event} e - The click event object.
     */
    handleDocJump(e) {
        e.preventDefault();

        const scrollPos = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
        const targetId = e.currentTarget.getAttribute(this.sectionAttribute);
        const targetElement = document.querySelector(`#${targetId}`);

        if (!targetElement) return;

        const targetPosition = targetElement.getBoundingClientRect().top + scrollPos - this.offset;

        window.scrollTo({
            top: targetPosition,
            behavior: 'smooth',
        });

        this.callbacks(e);
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const sidebarTOC = new TocHandler({
        tocContainerSelector: '.on-this-page--sidebar[data-toc]',
        offset: 50
    });

    const mobileTOC = new TocHandler({
        tocContainerSelector: '.on-this-page--mobile-toc[data-toc]',
        offset: 50,
        callbacks: () => window.ASB_App.dropdownHandler.closeDropdown('#mobile-toc-content')
    });

});
